import React from 'react';
import { Circle, Line, Rect, SchematicShell } from './SchematicPrimitives';
import { OUTLET } from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Receptacle family — duplex outlet / GFCI / 240V. The 2D elevation twin of
 * `electricalDeviceRenderers.js`: both read the face layout from `OUTLET` in
 * proceduralSpecs, so the slots sit in the same place on paper and in 3D.
 *
 * Front view (looking at the wall): the plate outline with its faces.
 *   - outlet: two faces, each two blade slots over a ground hole
 *   - gfci:   two faces further apart with TEST / RESET buttons between
 *   - 240v:   one round face with angled blades
 *
 * Side views: the thin plate against the wall; receptacles are flush, so
 * there is nothing to protrude. Symmetric, so left and right match.
 *
 * SVG y runs DOWN; the spec's vertical offsets run up from the plate center.
 */

export function OutletSchematic(props) {
  return <Receptacle {...props} kind="duplex" />;
}
OutletSchematic.supportsSideViews = true;

export function GfciOutletSchematic(props) {
  return <Receptacle {...props} kind="gfci" />;
}
GfciOutletSchematic.supportsSideViews = true;

export function Outlet240vSchematic(props) {
  return <Receptacle {...props} kind="240v" />;
}
Outlet240vSchematic.supportsSideViews = true;

function Receptacle({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
  kind,
}) {
  if (view === 'left' || view === 'right') {
    const plate = Math.min(OUTLET.plateDepth, Math.max(0, depth));
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <Rect
          x={0}
          y={0}
          width={plate}
          height={height}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      </SchematicShell>
    );
  }

  const stroke = { strokeColor, strokeWidth };
  const cx = width / 2;
  const cy = height / 2;

  return (
    <SchematicShell width={width} height={height} {...stroke}>
      {kind === '240v' ? (
        <Face240 width={width} height={height} cx={cx} cy={cy} {...stroke} />
      ) : (
        <>
          <DuplexFace
            width={width}
            height={height}
            faceY={
              cy -
              height *
                (kind === 'gfci'
                  ? OUTLET.gfciFaceOffsetFrac
                  : OUTLET.duplexFaceOffsetFrac)
            }
            {...stroke}
          />
          <DuplexFace
            width={width}
            height={height}
            faceY={
              cy +
              height *
                (kind === 'gfci'
                  ? OUTLET.gfciFaceOffsetFrac
                  : OUTLET.duplexFaceOffsetFrac)
            }
            {...stroke}
          />
          {kind === 'gfci' && (
            <GfciButtons width={width} height={height} cy={cy} {...stroke} />
          )}
        </>
      )}
    </SchematicShell>
  );
}

/** Two blade slots over a ground hole, centered on `faceY`. */
function DuplexFace({ width, height, faceY, strokeColor, strokeWidth }) {
  const slotH = height * OUTLET.slotHeightFrac;
  const spread = width * OUTLET.slotSpreadFrac;
  const cx = width / 2;
  const stroke = { strokeColor, strokeWidth };
  return (
    <g>
      <Line
        x1={cx - spread}
        y1={faceY - slotH / 2}
        x2={cx - spread}
        y2={faceY + slotH / 2}
        {...stroke}
      />
      <Line
        x1={cx + spread}
        y1={faceY - slotH / 2}
        x2={cx + spread}
        y2={faceY + slotH / 2}
        {...stroke}
      />
      <Circle
        cx={cx}
        cy={faceY + slotH * OUTLET.groundDropFrac}
        r={OUTLET.slotWidth}
        {...stroke}
      />
    </g>
  );
}

function GfciButtons({ width, height, cy, strokeColor, strokeWidth }) {
  const w = width * OUTLET.gfciButtonWidthFrac;
  const h = height * OUTLET.gfciButtonHeightFrac;
  const offset = width * OUTLET.gfciButtonOffsetFrac;
  return (
    <g>
      {[-offset, offset].map((dx) => (
        <Rect
          key={dx}
          x={width / 2 + dx - w / 2}
          y={cy - h / 2}
          width={w}
          height={h}
          strokeColor={strokeColor}
          strokeWidth={strokeWidth}
        />
      ))}
    </g>
  );
}

/** One round face with two angled blades and a ground slot. */
function Face240({ width, height, cx, cy, strokeColor, strokeWidth }) {
  const r = Math.min(width, height) * OUTLET.receptacle240RadiusFrac;
  const blade = r * 0.55;
  const tilt = Math.sin(Math.PI / 8) * (blade / 2);
  const stroke = { strokeColor, strokeWidth };
  const bladeLine = (x, dir) => (
    <Line
      x1={x - dir * tilt}
      y1={cy - r * 0.1 - blade / 2}
      x2={x + dir * tilt}
      y2={cy - r * 0.1 + blade / 2}
      {...stroke}
    />
  );
  return (
    <g>
      <Circle cx={cx} cy={cy} r={r} {...stroke} />
      {bladeLine(cx - r * 0.45, -1)}
      {bladeLine(cx + r * 0.45, 1)}
      <Line
        x1={cx - r * 0.12}
        y1={cy + r * 0.55}
        x2={cx + r * 0.12}
        y2={cy + r * 0.55}
        {...stroke}
      />
    </g>
  );
}
